import React, { useEffect, useRef } from 'react'
import { SeasonalMonthsPeriodType } from '../../Types';
import { CheckedIconColored, MenuDownIcon } from 'assests/GmsIcons';

type MonthSelectorProps = {
    isOpen: boolean
    selectedPeriods: SeasonalMonthsPeriodType[] | undefined;
    onToggle: () => void;
    onSelect: (period: SeasonalMonthsPeriodType) => void;
    periods: readonly SeasonalMonthsPeriodType[];
}

const MonthSelector = ({ isOpen, selectedPeriods, onToggle, onSelect, periods }: MonthSelectorProps) => {
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                if (isOpen) onToggle();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, onToggle]);

    const getDisplayText = () => {
        if (selectedPeriods?.includes('Custom Date')) {
            return 'Custom Date';
        }

        if (selectedPeriods?.length === 0) {
            return 'Month';
        }

        if (selectedPeriods?.length === 1) {
            return selectedPeriods[0];
        }

        return `${selectedPeriods?.length} months selected`;
    };

    return (
        <div className="tw-relative tw-mt-2" ref={dropdownRef}>
            <div
                onClick={onToggle}
                className={`tw-w-48 tw-px-2.5 tw-py-2 tw-bg-white tw-rounded tw-outline tw-outline-1 tw-outline-offset-[-1px] ${isOpen ? 'tw-outline-blue-950 tw-outline-[1.40px] tw-outline-offset-[-1.40px]' : 'tw-outline-gray-200'
                    } tw-inline-flex tw-justify-start tw-items-center tw-gap-2.5 tw-cursor-pointer`}
            >
                <div className="tw-flex-1 tw-justify-start tw-text-blue-950 tw-text-sm tw-font-semibold tw-leading-tight tw-whitespace-nowrap tw-overflow-hidden tw-text-ellipsis">
                    {getDisplayText()}
                </div>
                <MenuDownIcon />
            </div>

            {isOpen && (
                <div className="tw-absolute tw-z-10 tw-left-0 tw-top-full tw-w-48 tw-shadow-[0px_2px_4px_0px_rgba(0,0,0,0.25)] tw-inline-flex tw-flex-col tw-justify-start tw-items-start">
                    {periods.map((period, index) => {
                        const isSelected = selectedPeriods?.includes(period);
                        const isCustomDate = period === 'Custom Date';

                        return (
                            <div
                                key={period}
                                onClick={() => onSelect(period)}
                                className={`
                                       tw-self-stretch tw-h-9 tw-px-2.5 tw-py-2 tw-bg-white 
                                       tw-outline tw-outline-1 tw-outline-offset-[-1px] tw-outline-zinc-200 
                                       tw-inline-flex tw-justify-start tw-items-center tw-gap-2.5
                                       tw-cursor-pointer
                                       ${index === 0 ? 'tw-rounded-tl-[3px] tw-rounded-tr-[3px]' : ''}
                                       ${index === periods.length - 1 ? 'tw-rounded-bl tw-rounded-br' : ''}
                                   `}
                            >
                                {!isCustomDate && (
                                    <div className="tw-flex tw-items-center tw-justify-center tw-w-4 tw-h-4 tw-border tw-border-gray-300 tw-rounded tw-mr-2">
                                        {isSelected && (
                                            <svg
                                                className="tw-w-3 tw-h-3 tw-text-blue-950"
                                                fill="currentColor"
                                                viewBox="0 0 20 20"
                                            >
                                                <path
                                                    fillRule="evenodd"
                                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                    clipRule="evenodd"
                                                />
                                            </svg>
                                        )}
                                    </div>
                                )}

                                <div className={`
                                       ${isSelected ? 'tw-font-semibold' : ''} 
                                       tw-flex-1 tw-justify-start tw-text-blue-950 tw-text-sm tw-leading-tight
                                   `}>
                                    {period}
                                </div>

                                {isCustomDate && isSelected && (
                                    <CheckedIconColored />
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export default MonthSelector